import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Vertical } from '@gravitate-js/excalibrr'
import { Tabs } from 'antd'
import type { MatchDetailResponse } from '../../../../../api/useSupplierReconciliation/useMatchDetail'
import { MatchDetailPane } from './MatchDetailPane/MatchDetailPane'
import { MatchCommentsContainer } from '../MatchCommentsContainer'
import { MatchWarningsContainer } from '../MatchWarningsContainer'
import { MatchHistoryContainer } from '../MatchHistoryContainer'
import type { components } from '../../../../../types/api/v1'
import type { SupplierReconciliationMetadataResponse } from '../../../../../api/useMetadata/types'

type SRWarning = components['schemas']['SRWarning']

interface MatchDetailSidebarProps {
  match: MatchDetailResponse
  matchActions: {
    handleIndividualHold: (matchId: string, reasonCode?: string) => Promise<void>
    handleIndividualUnhold: (matchId: string) => Promise<void>
    handleIndividualUnapprove: (matchId: string) => Promise<void>
    handleIndividualApprove: (matchId: string, reasonCode: string) => Promise<void>
    handleVoidInvoice: (invoiceId: string) => Promise<void>
    handleUnvoidInvoice: (invoiceId: string) => Promise<void>
    approveMatchMutation: { mutate: (data: { match_id: string; reason_code: string }) => void; isLoading: boolean }
    waitingOnSupplierMutation: {
      mutate: (data: { match_id: string; reason_code?: string }) => void
      isLoading: boolean
    }
    voidInvoiceMutation: { mutate: (invoiceId: string) => void; isLoading: boolean }
    unvoidInvoiceMutation: { mutate: (invoiceId: string) => void; isLoading: boolean }
  }
  metadata?: SupplierReconciliationMetadataResponse
  metadataLoading: boolean
  modelConfig?: {
    approve_reason_required?: boolean
    hold_reason_required?: boolean
  }
}

export function MatchDetailSidebar({
  match,
  matchActions,
  metadata,
  metadataLoading,
  modelConfig,
}: MatchDetailSidebarProps) {
  const { id } = useParams()

  // Transform warnings for display - in details view, show all warnings including delivery reconciliation
  const transformedWarnings = useMemo(() => {
    const warnings =
      match.warnings?.map((warning) => ({
        ...warning,
        formattedMessage: getWarningMessage(warning),
        severity: 'warning' as const,
      })) || []

    // In details view, show all warnings (unlike main grid where movement_validation and delivery_reconciliation are filtered out)
    return warnings
  }, [match.warnings, modelConfig])

  return (
    <Vertical flex={1} bg='default' style={{ height: '100%', overflow: 'hidden' }}>
      {/* Left Detail Pane */}
      <Vertical flex='1' style={{ minHeight: 0, overflow: 'hidden' }}>
        <MatchDetailPane
          match={match}
          matchActions={matchActions}
          metadata={metadata}
          metadataLoading={metadataLoading}
          modelConfig={modelConfig}
        />
      </Vertical>

      {/* Bottom Tabs Section */}
      <Vertical flex='0 0 48%' bg='default' border='all' borderRadius='6px' style={{ minHeight: 0 }}>
        <Tabs
          defaultActiveKey='comments'
          style={{ height: '100%' }}
          tabBarStyle={{
            padding: '0 16px',
            marginBottom: '8px',
          }}
          items={[
            {
              key: 'comments',
              label: `Comments${match?.comments && match.comments.length > 0 ? ` (${match.comments.length})` : ''}`,
              children: <MatchCommentsContainer matchId={id || ''} comments={match?.comments || []} />,
            },
            {
              key: 'warnings',
              label: `Warnings${transformedWarnings.length > 0 ? ` (${transformedWarnings.length})` : ''}`,
              children: <MatchWarningsContainer warnings={transformedWarnings} modelConfig={modelConfig} />,
            },
            {
              key: 'history',
              label: 'History',
              children: <MatchHistoryContainer history={match?.activity} match={match} />,
            },
          ]}
        />
      </Vertical>
    </Vertical>
  )
}

// Helper function for warning messages
function getWarningMessage(warning: SRWarning): string {
  const variance = warning.variance || 0
  const absVariance = Math.abs(variance)
  const direction = variance > 0 ? 'higher' : 'lower'

  const messageMap: Record<string, string> = {
    terminal_mismatch: 'Terminal mismatch detected',
    supplier_mismatch: 'Supplier mismatch detected',
    gross_volume_mismatch: `Gross volume is ${absVariance.toFixed(2)} gallons ${direction} than expected`,
    net_volume_mismatch: `Net volume is ${absVariance.toFixed(2)} gallons ${direction} than expected`,
    total_amount_mismatch: `Total amount is $${absVariance.toFixed(2)} ${direction} than expected`,
    total_per_volume_price_mismatch: `Per unit price is $${absVariance.toFixed(2)} ${direction} than expected`,
    total_product_cost_mismatch: `Product cost is $${absVariance.toFixed(2)} ${direction} than expected`,
    total_tax_mismatch: `Tax total is $${absVariance.toFixed(2)} ${direction} than expected`,
    delivery_reconciliation: 'Delivery reconciliation errors must be resolved',
    movement_validation: 'Order movement validation issues detected',
  }

  return messageMap[warning.type] || `Variance of ${absVariance.toFixed(2)} detected`
}
